// Column definitions + cell renderers shared by List and Table views.
import { memo } from 'react';
import type { CustomField, Task, ViewColumn } from '@shared/types';
import { builtinField } from '@shared/constants';
import { useDb } from '@/lib/store';
import { trackedMs, updateTask } from '@/lib/actions';
import { formatDateShort, formatDuration } from '@/lib/format';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { Popover } from '@/components/ui/Popover';
import { CustomFieldCell } from '@/components/fields/CustomField';
import {
  AssigneeCell,
  DueDateCell,
  PriorityCell,
  StartDateCell,
  StatusCell,
  TagsCell,
  TaskTypeMenu,
  TimeEstimateCell,
} from '@/components/fields/TaskCells';
import { FIELD_TYPE_ICONS } from '@/components/ui/Icon';

export interface ColumnDef {
  id: string;
  label: string;
  width: number;
  icon: React.ReactNode;
  field?: CustomField;
}

export function resolveColumns(columns: ViewColumn[], customFields: Record<string, CustomField>, available: Set<string>): ColumnDef[] {
  const out: ColumnDef[] = [];
  for (const c of columns) {
    if (c.hidden) continue;
    if (c.id.startsWith('cf:')) {
      const f = customFields[c.id.slice(3)];
      if (!f || !available.has(f.id)) continue;
      const I = FIELD_TYPE_ICONS[f.type];
      out.push({ id: c.id, label: f.name, width: c.width ?? 170, icon: <I size={13} />, field: f });
    } else {
      const b = builtinField(c.id);
      if (!b || b.id === 'name') continue;
      out.push({ id: c.id, label: b.label, width: c.width ?? b.width, icon: <Icon name={b.icon} size={13} /> });
    }
  }
  return out;
}

function LatestComment({ taskId }: { taskId: string }) {
  const latest = useDb((db) => {
    let best: { body: string; at: number } | null = null;
    for (const c of Object.values(db.comments)) if (c.taskId === taskId && (!best || c.createdAt > best.at)) best = { body: c.body, at: c.createdAt };
    return best?.body ?? null;
  });
  if (!latest) return <span className="empty-dash">–</span>;
  const text = latest.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return <span className="truncate muted">{text}</span>;
}

function Progress({ task }: { task: Task }) {
  const pct = useDb((db) => {
    const subs = Object.values(db.tasks).filter((t) => t.parentId === task.id && !t.archived);
    const items = task.checklists.flatMap((c) => c.items);
    const total = subs.length + items.length;
    if (!total) return null;
    const done = subs.filter((t) => t.closedAt).length + items.filter((i) => i.resolved).length;
    return Math.round((done / total) * 100);
  });
  if (pct === null) return <span className="empty-dash">–</span>;
  return (
    <span className="progress-cell">
      <span className="progress-bar">
        <span style={{ width: `${pct}%` }} />
      </span>
      <span className="faint">{pct}%</span>
    </span>
  );
}

function TimeTracked({ taskId }: { taskId: string }) {
  const ms = useDb((db) => trackedMs(db, taskId));
  return ms ? <span>{formatDuration(ms)}</span> : <span className="empty-dash">–</span>;
}

function TaskTypeCell({ task }: { task: Task }) {
  const tt = useDb((db) => (task.taskTypeId ? db.taskTypes[task.taskTypeId] : Object.values(db.taskTypes).find((t) => t.builtin === 'task')));
  return (
    <Popover
      content={(close) => (
        <TaskTypeMenu
          workspaceId={task.workspaceId}
          value={task.taskTypeId}
          onPick={(id) => {
            updateTask(task.id, { taskTypeId: id });
            close();
          }}
        />
      )}
    >
      <button className="cell-button">
        {tt && <Icon name={tt.icon} size={14} color={tt.color ?? undefined} />}
        <span className="truncate">{tt?.name ?? 'Task'}</span>
      </button>
    </Popover>
  );
}

export const Cell = memo(function Cell({ task, column, spaceId }: { task: Task; column: ColumnDef; spaceId: string }) {
  const users = useDb((db) => db.users);
  const listName = useDb((db) => (column.id === 'list' ? db.lists[task.listId]?.name : undefined));
  switch (column.id) {
    case 'assignee':
      return <AssigneeCell task={task} />;
    case 'dueDate':
      return <DueDateCell task={task} />;
    case 'startDate':
      return <StartDateCell task={task} />;
    case 'priority':
      return <PriorityCell task={task} />;
    case 'status':
      return <StatusCell task={task} size="sm" />;
    case 'tags':
      return <TagsCell task={task} spaceId={spaceId} />;
    case 'timeEstimate':
      return <TimeEstimateCell task={task} />;
    case 'timeTracked':
      return <TimeTracked taskId={task.id} />;
    case 'dateCreated':
      return <span className="cell-text">{formatDateShort(task.createdAt)}</span>;
    case 'dateUpdated':
      return <span className="cell-text">{formatDateShort(task.updatedAt ?? task.createdAt)}</span>;
    case 'dateClosed':
      return task.closedAt ? <span className="cell-text">{formatDateShort(task.closedAt)}</span> : <span className="empty-dash">–</span>;
    case 'createdBy':
      return (
        <span className="cell-text row gap-6">
          <Avatar userId={task.createdBy} size={20} />
          <span className="truncate">{users[task.createdBy]?.name}</span>
        </span>
      );
    case 'latestComment':
      return <LatestComment taskId={task.id} />;
    case 'taskType':
      return <TaskTypeCell task={task} />;
    case 'list':
      return <span className="cell-text truncate">{listName}</span>;
    case 'taskId':
      return <span className="cell-text faint mono">#{task.id}</span>;
    case 'progress':
      return <Progress task={task} />;
    default:
      return column.field ? <CustomFieldCell task={task} field={column.field} /> : null;
  }
});
